/* eslint-disable camelcase */
import { ContentState, convertToRaw } from "draft-js";
import moment, { Moment } from "moment";
import { Utils } from "../services/utils";
import { FilterData } from "../services/filters/filterData";
import { ChecklistData } from "./checklistData";
import { DocumentData, DocumentIdData } from "./documentData";
import { MyELearningData } from "./elearning";
import { EventStatus } from "./enums/eventStatus.enum";
import { EventType, InternalExternalEventType } from "./enums/eventType.enum";
import { ParticipationStatus } from "./enums/participationStatus.enum";
import { EventsSortBy } from "./enums/sortBy.enum";
import { TrainingType } from "./enums/trainingType.enum";
import { SpeakerBaseData } from "./speakerData";
import {
  ParticipationType,
  TargetGroupIdData,
  TargetGroupListData,
} from "./targetGroupData";
import { SimpleTaskData } from "./taskData";
import { NIL as NIL_UUID } from "uuid";
import { DiscountItem } from "./discountData";
import { CertificateTypeData } from "./certificateTypeData";
import {
  CourseLanguageData,
  CourseSubtitleData,
  CourseTopicData,
  CourseTagsData,
} from "./courseData";

export interface MyEventData extends TargetGroupIdData {
  id: string;
  public_id: number;
  title: string;
  location: string;
  location_details: string;
  begin: Date;
  end: Date;
  score: number;
  price: number;
  description: string;
  training_content: string;
  min_seats: number;
  max_seats: number;
  status: number;
  event_type: number;
  training_type: number;
  speakers: string[];
  documents?: DocumentData[];
  within_cancellation_period: boolean;
  evaluation_link?: string;
  max_time_period_to_finish_elearning_module_in_weeks?: number;
  publish_elearning_after_event?: boolean;
}

export interface UserLinkedAccountStats {
  user: string;
  status: ParticipationStatus;
}

export interface ActivityHistoryMinimalEventData {
  id: string;
  public_id: number;
  title: string;
  training_type: TrainingType;
  location: string;
}

export interface ActivityHistoryEventData extends ActivityHistoryMinimalEventData {
  location: string;
  begin: Date;
  end: Date;
  training_type: TrainingType;
  event_type: EventType;
  max_time_period_to_finish_elearning_module_in_weeks?: number;
  publish_elearning_after_event?: boolean;
}

export interface EventListData extends TargetGroupIdData {
  url: string;
  id: string;
  vnr: string;
  public_id: number;
  title: string;
  location: string;
  begin: Date | null;
  end: Date | null;
  score: number;
  price: number;
  seats: number;
  seats_taken: number;
  status: number;
  internal_external: InternalExternalEventType;
  event_type: number;
  training_type: number;
  participation_status: number;
  participation_status_linked_accounts: UserLinkedAccountStats[];
  description: string;
  training_content: string;
  speakers: SpeakerBaseData[];
  is_invoiced?: boolean;
  has_open_tasks?: boolean;
  participants_status_set?: boolean;
  is_favorite: boolean;
  evaluation_link?: string;
  evaluation_link_speaker?: string;
  is_my_target_group?: boolean;
  total_amount: number;
  vat_amount: number;
  value_added_tax_rate: number | undefined;
  number_of_units?: number | undefined;
  duration_per_unit_in_minutes?: number | undefined;
}

export interface MyEventListData {
  id: string;
  event: MyELearningData;
  status: ParticipationStatus;
  created_on: Date;
  last_changed_on: Date;
  last_changed_by: string;
  user_has_clicked_evaluation_link: boolean;
  overall_begin: Date;
  overall_end: Date;
  participation_certificate_was_downloaded: boolean;
  participation_certificate_availability_date: Date | null;
  access_granted_by_admin?: boolean;
}

export interface EventFormData extends TargetGroupIdData {
  vnr: string;
  title: string;
  score: number;
  price: number;
  minseats: number;
  maxseats: number;
  status: number;
  internalExternalEvent: InternalExternalEventType;
  description: ContentState;
  descriptionState: ContentState;
  training_content: ContentState;
  trainingContentState: ContentState;
  speakers: SpeakerBaseData[];
  location: string;
  city: string;
  zip_code: string;
  locationDetails: string;
  eventType: number;
  trainingType: number;
  beginDate: Moment | null;
  endDate: Moment | null;
  beginTime: Moment | null;
  endTime: Moment | null;
  catering: boolean;
  documents: string[];
  commentary: string;
  participation?: number;
  searchSpeakers: string;
  submitWithTemplate?: boolean;
  tasks: SimpleTaskData[];
  checklist: ChecklistData[];
  checklist_additional_info: string;
  evaluationLink: string;
  evaluationLinkSpeaker: string;
  value_added_tax_rate?: number;
  discounts: DiscountItem[];
  is_certification_selected: boolean;
  certificate_type: CertificateTypeData;
  course_language: CourseLanguageData;
  course_subtitle: CourseSubtitleData;
  topic: CourseTopicData;
  tags: CourseTagsData[];
  has_sign_language: boolean;
}
export interface CrudEventDataDto extends TargetGroupListData {
  public_id?: number;
  vnr: string;
  title: string;
  location: string;
  city: string | null;
  zip_code: string | null;
  location_details: string;
  begin: Date | null;
  end: Date | null;
  score: number;
  price: number;
  description: string;
  training_content: string;
  min_seats: number;
  max_seats: number;
  status: number;
  internal_external: InternalExternalEventType;
  catering: boolean;
  event_type: number;
  training_type: number;
  speakers: SpeakerBaseData[];
  commentary: string;
  is_invoiced?: boolean;
  has_open_tasks?: boolean;
  participants_status_set?: boolean;
  documents?: DocumentIdData[];
  tasks: SimpleTaskData[];
  evaluation_link?: string;
  evaluation_link_speaker?: string;
  template: string | null;
  value_added_tax_rate?: number;
  discounts: DiscountItem[];
  is_certification_selected: boolean;
  certificate_type: CertificateTypeData;
  course_language: CourseLanguageData;
  course_subtitle: CourseSubtitleData;
  topic: CourseTopicData;
  tags: CourseTagsData[];
  has_sign_language: boolean;
}

export interface EventDataDto extends CrudEventDataDto {
  url: string;
  id: string;
  checklist: ChecklistData[];
  checklist_additional_info: string;
  evaluation_link?: string;
  max_seats_taken: boolean;
  discounts: DiscountItem[];
  certificate_type: CertificateTypeData;
  training_content: string;
}

export interface EventFilterValues {
  locations: string[];
}

export interface EventFilterData extends FilterData<EventsSortBy>, TargetGroupIdData {
  search: string;
  participation_types: string[];
  event_type: number[];
  training_type: number[];
  location: string[];
  status: string;
  my_target_group: boolean;
  begin_gte: string;
  begin_lte: string;
  language_filters: string[];
  thematic_filters: string[];
  target_group_filters: string[];
}

export interface EventListPossibleFilters {
  public_id?: number;
  location?: string[];
  begin?: Date;
  status?: string;
}

export function mapAddEventToDto(
  data: EventFormData,
  templateId?: string
): CrudEventDataDto {
  const addEventDataDto: CrudEventDataDto = {
    vnr: data.vnr,
    title: data.title,
    location: data.location,
    city: data.eventType === EventType.OnSite ? data.city : null,
    zip_code: data.eventType === EventType.OnSite ? data.zip_code : null,
    location_details: data.locationDetails,
    begin: mapDateAndTime(data.beginDate, data.beginTime),
    end: mapDateAndTime(data.endDate, data.endTime),
    score: data.score,
    price: parseFloat((data.price * 100).toFixed(2)),
    description: JSON.stringify(convertToRaw(data.description)),
    training_content: JSON.stringify(convertToRaw(data.training_content)),
    min_seats: data.minseats,
    max_seats: data.maxseats,
    status: data.status,
    internal_external: data.internalExternalEvent,
    event_type: data.eventType,
    training_type: data.trainingType,
    participation_types: mapToParticipationTypeList(data.participation_types),
    speakers: data.speakers,
    documents: mapToDocumentList(data.documents),
    commentary: data.commentary,
    tasks: data.tasks,
    catering: data.catering,
    evaluation_link: data.evaluationLink,
    evaluation_link_speaker: data.evaluationLinkSpeaker,
    template: templateId ? templateId : null,
    value_added_tax_rate: data.value_added_tax_rate,
    discounts: data.discounts,
    is_certification_selected: data.is_certification_selected,
    certificate_type: data.certificate_type,
    course_language: data.course_language,
    course_subtitle: data.course_subtitle,
    has_sign_language: data.has_sign_language,
    tags: data.tags,
    topic: data.topic,
  };
  return addEventDataDto;
}

export function mapEditEventDtoToEventForm(data: EventDataDto): EventFormData {
  const editEventFormData: EventFormData = {
    title: data.title,
    vnr: data.vnr,
    score: data.score,
    price: data.price / 100,
    minseats: data.min_seats,
    maxseats: data.max_seats,
    participation_types: mapToStringList(data.participation_types),
    status: data.status,
    internalExternalEvent: data.internal_external,
    description: Utils.convertJsonToRteState(data.description),
    descriptionState: Utils.convertJsonToRteState(data.description),
    training_content: Utils.convertJsonToRteState(data.training_content),
    trainingContentState: Utils.convertJsonToRteState(data.training_content),
    speakers: data.speakers,
    location: data.location,
    city: data.city || "",
    zip_code: data.zip_code || "",
    locationDetails: data.location_details,
    eventType: data.event_type,
    trainingType: data.training_type,
    beginDate: moment(data.begin),
    endDate: moment(data.end),
    beginTime: moment(data.begin),
    endTime: moment(data.end),
    catering: data.catering,
    documents: mapDocumentToStringList(data.documents ?? []),
    commentary: data.commentary,
    searchSpeakers: "",
    tasks: data.tasks,
    checklist: data.checklist,
    checklist_additional_info: data.checklist_additional_info,
    evaluationLink: data.evaluation_link || "",
    evaluationLinkSpeaker: data.evaluation_link_speaker || "",
    value_added_tax_rate: data.value_added_tax_rate,
    discounts: data.discounts,
    is_certification_selected: data.is_certification_selected,
    certificate_type: data.certificate_type,
    course_language: data.course_language,
    course_subtitle: data.course_subtitle,
    has_sign_language: data.has_sign_language,
    tags: data.tags,
    topic: data.topic,
  };
  return editEventFormData;
}
export function mapEventFormDataToEventListData(data: EventFormData): EventListData {
  const previewEventData: EventListData = {
    url: "",
    id: NIL_UUID,
    vnr: "",
    public_id: 0,
    title: data.title,
    location: data.location,
    begin: mapDateAndTime(data.beginDate, data.beginTime),
    end: mapDateAndTime(data.endDate, data.endTime),
    score: data.score,
    price: parseFloat((data.price * 100).toFixed(2)),
    seats: 10,
    seats_taken: 0,
    event_type: data.eventType,
    training_type: data.trainingType,
    status: EventStatus.Published,
    internal_external: data.internalExternalEvent,
    participation_status: ParticipationStatus.NotParticipated,
    participation_status_linked_accounts: [],
    description: JSON.stringify(convertToRaw(data.description)),
    training_content: JSON.stringify(convertToRaw(data.training_content)),
    participation_types: data.participation_types,
    speakers: data.speakers,
    has_open_tasks: true, //Dont show DoneIcon in Preview
    is_favorite: false,
    total_amount: 0,
    vat_amount: 0,
    value_added_tax_rate: data.value_added_tax_rate,
  };
  return previewEventData;
}

export function mapDateAndTime(
  dateData: Moment | null,
  timeData: Moment | null
): Date | null {
  let combineDateTime = null;
  if (dateData !== null) {
    combineDateTime = moment(dateData);
    if (timeData !== null) {
      combineDateTime.hours(timeData.hours());
      combineDateTime.minutes(timeData.minutes());
    }
  }
  return combineDateTime !== null ? combineDateTime.toDate() : null;
}

function mapToStringList(participation_types: ParticipationType[]): string[] {
  return participation_types?.map((v) => v.id);
}

function mapToParticipationTypeList(participation_types: string[]): ParticipationType[] {
  return participation_types?.map((v) => ({
    id: v,
    document_type: "",
    label: "",
    is_default: false,
  }));
}

function mapDocumentToStringList(documents: DocumentIdData[]): string[] {
  return documents?.map((v) => v.id);
}

function mapToDocumentList(documents: string[]): DocumentIdData[] {
  return documents?.map((v) => ({ id: v }));
}
